// Davini.ai pitch deck — translations
// Codes (S.E.M., R.E.M., P1, M4) are proprietary and kept untranslated.

export const translations = {
    en: {
        nav: {
            problem: "Problem",
            solution: "Solution",
            product: "Product",
            metrics: "Metrics",
            pricing: "Pricing",
            team: "Team",
            present: "Present",
            pdf: "PDF",
            demo: "Demo",
        },
        hero: {
            eyebrow: "Tactical intelligence for tennis · Live in production",
            headlinePart1: "Outsmart",
            headlinePart2: "the opponent.",
            headlineAccent: "Point by point.",
            sub: "Every platform shows you what happened. Davini turns match data into decisions — what to train, why it matters, and how much it will move your results. Built on Franco Davín's Grand Slam-winning methodology.",
            ctaPrimary: "Request early access",
            ctaSecondary: "See the platform",
            stats: [
                ["8", "Analytics dimensions"],
                ["230+", "Performance metrics / match"],
                ["1,100+", "Matches analyzed"],
            ],
            semLabel: "S.E.M. — Serve Effectiveness Margin",
            semSub: "Sample: ITF-classified junior",
            semCaption: "of serve points won (S.E.M.)",
            verdictTitle: "Coach verdict",
            verdictBody:
                '"You\'ll likely get broken in 1 of every 3 service games at your current level."',
            verdictBold: "1 of every 3 service games",
            proprietary: "Proprietary",
            live: "Live",
            legacy: "Coaching legacy:",
            legacyBody: "Franco Davín · coach of del Potro (US Open 2009) & Gaudio (Roland Garros 2004)",
            marquee: [
                "20+ pro players supported",
                "ITF Research Collaboration",
                "Grand Slam Coaching Methodology",
                "Multi-Surface Tested",
                "250,000+ match statistics computed",
            ],
        },
        problem: {
            tag: "01 — The Problem",
            headline: "Tennis has more data than ever,",
            headlineAccent: "but data doesn't win matches.",
            sub: "Hawk-Eye, UTR, video tagging — the data already exists. What's missing is the layer that turns numbers into a decision: what to train next, and why. That's the layer Davini was built to be.",
            cards: [
                {
                    title: "From data to decisions",
                    body: "Numbers only matter when they answer coaching questions. Which errors actually cost the match? Where did momentum turn? At what rally length does your player win?",
                    tag: "Decision layer",
                },
                {
                    title: "Beyond traditional statistics",
                    body: "Aces and winners tell you what happened. Deuce/ad splits, effectiveness margins and determinant errors tell you what to do about it — the depth that decides elite matches.",
                    tag: "Tactical depth",
                },
                {
                    title: "Long-term performance intelligence",
                    body: "Track every metric across 10+ matches and a full season. Development becomes a trend line, not a guess — and you can prove the training is working.",
                    tag: "Season-long view",
                },
            ],
        },
        solution: {
            tag: "02 — The Solution",
            headline: "From raw match data to your next training decision in",
            headlineAccent: "3 steps.",
            steps: [
                {
                    n: "01",
                    title: "Upload",
                    body: "Import your match data and let Davini transform it into tactical insights and training priorities.",
                    meta: "CSV Template · Live Capture",
                },
                {
                    n: "02",
                    title: "Analyze",
                    body: "Our engine turns every point into 230+ performance metrics across 8 analytics dimensions — then distills them down to what actually matters.",
                    meta: "230+ metrics · 8 dimensions",
                },
                {
                    n: "03",
                    title: "Dominate",
                    body: "Not more numbers to interpret. Coach-calibrated verdicts, 10-match baselines and predictive margins tell you exactly what to train next — and what it's worth.",
                    meta: "Coach verdicts · Match plans",
                },
            ],
            csvLabel: "input · match.csv",
            engine: "Davini engine →",
        },
        dimensions: {
            tag: "03 — The Product",
            headline: "8 analytics dimensions",
            headlineAccent: "per player.",
            sub: "Eight dedicated tabs, each answering a different coaching question — from who controlled the match to where the next point is won. Plus Flow and 3D Court views, and a 12-page PDF report delivered to your inbox after every match.",
            cards: [
                {
                    code: "PERFORMANCE",
                    tagline: "Who controlled the match.",
                    body: "Leadership Margin, Match Quality rankings, performance trend lines across 10+ opponents, 6-axis Skillset Radar.",
                },
                {
                    code: "P1 — PRIORITY ERRORS",
                    tagline: "The errors that actually decided the match.",
                    body: "Determinant Unforced Error rate, Lost Points in a Row analysis, consecutive error pattern detection.",
                },
                {
                    code: "M4 — MINIMATCHES",
                    tagline: "Win % on 1st & 2nd serve and return.",
                    body: 'Four effectiveness bars with sample size and coach-calibrated verdicts: "If you keep these values the probability of winning matches is low."',
                },
                {
                    code: "RALLY",
                    tagline: "Where the player wins or bleeds points.",
                    body: "Rally length distribution across 6 serve/return scenarios. Win % per rally bucket from 0 to 15+ shots.",
                },
                {
                    code: "SERVE",
                    tagline: "A full wall of serve charts, every match.",
                    body: "1st Serve In %, points won on 1st/2nd serve, deuce vs ad court splits, running averages and trends.",
                },
                {
                    code: "S.E.M.",
                    tagline: "Serve Effectiveness Margin.",
                    body: "Proprietary composite metric that predicts how often the serve will be broken. Coach-calibrated verdicts. Tracked over time to measure improvement.",
                },
                {
                    code: "RETURN",
                    tagline: "Mirrors the serve dimension.",
                    body: "1st/2nd Serve Return In %, Return Effectiveness, deuce/ad splits — 10 charts tracking return strength.",
                },
                {
                    code: "R.E.M.",
                    tagline: "Return Effectiveness Margin.",
                    body: "Proprietary composite metric that predicts how often the opponent's serve will be broken. The complement to S.E.M. — together they predict match outcomes.",
                },
            ],
            pills: [
                "3D Court Visualization with Shot Filters",
                "Match Quality Score",
                "Break Point Analysis",
                "Report Builder",
                "CSV Export",
                "Multi-Surface Filtering",
                "10-Match Self-Benchmarking",
                "Automated PDF Match Reports (emailed after every match)",
                "WTN & UTR Rating Auto-Sync",
                "English / Español",
            ],
            proprietary: "Proprietary",
            catalogTag: "The catalog · 24 tactical metrics",
            catalogTitle: "Every metric Davini tracks",
            catalogSub: "Every match generates this full set of metrics — across all 8 dimensions.",
        },
        metrics: {
            tag: "04 — Proprietary Metrics",
            headline: "Metrics built to answer one question:",
            headlineAccent: "what do we train next?",
            sub: "Tennis-specific tactical metrics built around Franco Davín's coaching framework. Sample analysis below uses real anonymized data from an 18-year-old European ITF junior (24+ tracked competitive matches). Shown for illustration.",
            semVerdict:
                '"Serve will probably be broken in 1 of 3 games." The serve-points-won margin that predicts how often you\'ll be broken.',
            remVerdict:
                '"High probability of breaking opponent in 1 of 3 games." The return-points-won margin that predicts how often you\'ll break.',
            p1Verdict:
                "The % of unforced errors that are Determinant — the errors that actually decided the match.",
            leadershipLabel: "Leadership Margin",
            leadershipCaption: "10 matches",
            leadershipPointsLabel: "Points controlled",
            leadershipBody:
                "Who controlled the match point-by-point — tracked across 10+ opponents.",
            rallyLabel: "Rally Win — most frequent rally bucket",
            rallyCaption: "57% won @ 0–2 shots",
            rallyBody:
                "57% of points won at the most frequent rally length (0–2 shots) — reveals the player's ideal tactical game length.",
            p1Heading: "P1 — Priority 1",
            proprietary: "Proprietary",
            simTag: "The Simulation Engine",
            simTitle: "Simulate the improvement before you train it.",
            simBody: "Pick a priority error. Dial it down. Davini recomputes Match Performance, S.E.M., R.E.M. and Minimatches across the entire match history — showing how much each fix moves results before you spend a single week on court.",
            simCase: "Real case: a former ATP top-40 player was losing ~12 points per match to emotional lapses. The simulation showed that cutting those to 7 projected a top-20-level performance margin — so the coaching target became winning the single point after each lapse. The metric made the coaching call.",
            simChip: "Proprietary · What-if analysis",
        },
        dashboard: {
            tag: "05 — Match Dashboard",
            headline: "Every match becomes a",
            headlineAccent: "tactical report.",
            sub: "Interactive replay, instant rankings, skillset radar and shot-level history — every match ends with a clear read: what worked, what didn't, and what to train this week.",
            playerLabel: "Sample profile · ITF junior",
            playerSub: "European ITF junior · 18 years old · Right-handed",
            perfTitle: "Match Performance · Last 10",
            perfMargin: "desired margin: +6",
            skillsetTitle: "Skillset Radar",
            skillsetSub: "6-axis profile",
            fhbhTitle: "FH / BH Key Shots — Historical",
            minimatchTitle: "Minimatches — Effectiveness",
            qualityTitle: "Top Match Quality",
            featurePills: [
                "3D Court Visualization with Shot Filters",
                "Match Quality Score",
                "6-axis Skillset Radar",
                "Minimatches Breakdown",
                "Deuce/Ad Court Splits",
                "FH / BH Shot Analysis",
            ],
            forehand: "Forehand",
            backhand: "Backhand",
        },
        pricing: {
            tag: "06 — Pricing",
            headline: "Plans for players, coaches,",
            headlineAccent: "academies & pro teams.",
            sub: "Early access pricing for coaches and academies. Davini scales without hardware costs, installation fees, or hidden tiers.",
            perMonth: "/mo",
            popular: "Most Popular",
            earlyAccess: "Early access",
            tiers: [
                {
                    name: "Player",
                    price: "$29",
                    tagline: "Best for competitive players tracking their own development.",
                    features: [
                        "Personal match analytics dashboard",
                        "Up to 10 matches / month",
                        "All 8 analytics dimensions",
                        "Performance trends across last 10 matches",
                        "P1 error analysis",
                        "Skillset radar & rally length charts",
                        "Mobile-friendly reports",
                    ],
                    cta: "Request access",
                },
                {
                    name: "Coach",
                    price: "$99",
                    tagline:
                        "Best for private and traveling coaches managing multiple players.",
                    features: [
                        "Everything in Player",
                        "Unlimited matches",
                        "Multi-player management (up to 10)",
                        "Full S.E.M. / R.E.M. metrics",
                        "P1 error analysis",
                        "Custom report builder",
                        "Priority support",
                    ],
                    cta: "Request access",
                },
                {
                    name: "Academy & Pro",
                    price: "$499",
                    tagline: "Best for tennis academies, federation programs, and pro teams.",
                    features: [
                        "Everything in Coach",
                        "Unlimited player profiles",
                        "Team-wide analytics & benchmarking (coming soon)",
                        "API access for custom integrations (coming soon)",
                        "Dedicated account manager",
                        "White-label reporting (coming soon)",
                    ],
                    cta: "Talk to us",
                },
            ],
        },
        competitive: {
            tag: "07 — What We Bring",
            headline: "How Davini.ai",
            headlineAccent: "is different.",
            sub: "Every other platform answers “what happened?” Davini answers “what should we do next?” — elite methodology, tennis-specific tactical intelligence and automated coaching verdicts in one workflow. No hardware, no installation.",
            feature: "Capability",
            cols: ["Davini.ai", "Hawk-Eye", "SwingVision", "Manual Stats"],
            rows: [
                "Point-by-point analytics",
                "8 tactical analytics dimensions",
                "S.E.M. / R.E.M. metrics",
                "P1 error classification",
                "Multi-match trends (10+)",
                "Automated coaching verdicts (calibrated by a Grand Slam coach)",
                "Accessible for individual coaches",
                "Works without hardware",
                "Coach-designed workflow",
            ],
            partial: "partial",
            quote: "Built with the coaching methodology of",
            quoteAccent: "Franco Davín — coach of two Grand Slam champions: Juan Martín del Potro (2009 US Open) and Gastón Gaudio (2004 Roland Garros).",
            quoteRest: "",
            tagline: "Davini.ai — Every platform gives you data. Davini tells you what wins matches.",
            footnote:
                "Comparison based on publicly available product information as of December 2025. Competitor products continue to evolve — please verify before purchase.",
        },
        traction: {
            tag: "08 — Traction",
            headline: "Already making",
            headlineAccent: "an impact.",
            stats: [
                ["20+", "Professional players supported"],
                ["1,100+", "Competitive matches analyzed"],
                ["230+", "Performance metrics per match"],
                ["250,000+", "Match statistics computed"],
            ],
            milestones: [
                "Co-developed with Franco Davín — former coach of two Grand Slam champions: Juan Martín del Potro (2009 US Open) and Gastón Gaudio (2004 Roland Garros).",
                "Validated with ITF-classified juniors across hard, clay, and indoor surfaces.",
                "Platform live & deployed in production with all 8 analytics dimensions operational.",
            ],
            trustedTitle: "Trusted at every level of the game",
            trustedItems: [
                "WTA & ATP tour professionals — including multiple top-20 players",
                "A reigning junior Grand Slam champion",
                "ITF-classified juniors across three continents",
            ],
            orgsNote: "Research collaboration with the International Tennis Federation (ITF). Also used by Davis Cup and Billie Jean King Cup national teams, COSAT (South American Tennis Confederation), and leading academies throughout South America.",
        },
        team: {
            tag: "09 — The Team",
            headline: "Built by coaches,",
            headlineAccent: "for coaches.",
            sub: "Davini.ai was born on the practice courts of elite tennis. We didn't build another stats app — we built the analytics platform we wished existed when coaching at the highest level.",
            members: [
                {
                    name: "Franco Davín",
                    role: "Co-Founder & CEO",
                    sub: "Head of Coaching Methodology",
                    bio: "Former coach of two Grand Slam champions — Juan Martín del Potro (2009 US Open) and Gastón Gaudio (2004 Roland Garros). Decades of ATP Tour coaching experience. Architect of the analytical framework behind every Davini metric — P1, S.E.M., R.E.M., Leadership Margin.",
                },
                {
                    name: "Boon Chew",
                    role: "Co-Founder & CTO",
                    sub: "Product & Engineering",
                    bio: "Boon Chew is a venture builder and founder of Tappollo Media and Centaur Lab, focused on launching and scaling technology and AI companies. He has helped create platforms behind startups valued at over $3 billion and partnered with global brands including iHeartRadio, Discovery, CLEAR, and SEVENROOMS. His work spans high-growth consumer products and award-winning social impact platforms, including the first dating app for the autism community and Pepper, a social platform with over 2 million downloads.",
                },
            ],
        },
        cta: {
            eyebrow: "Ready to coach like a pro?",
            headlinePart1: "Win the match",
            headlineAccent: "before",
            headlinePart2: "it starts.",
            sub: "Request early access. Upload one match and get your first coach-calibrated read — what to train, why, and what it's worth.",
            primary: "Request early access",
            secondary: "Talk to us",
            footnote:
                "Coaching-first workflow · No hardware required · Designed around Franco Davín's methodology",
        },
        footer: {
            copyright: "© {year} Davini.ai — Tactical analytics for tennis.",
            product: "Product",
            pricing: "Pricing",
            team: "Team",
        },
        presenter: {
            slides: [
                "Cover",
                "Problem",
                "Solution",
                "8 Dimensions",
                "Proprietary Metrics",
                "Match Dashboard",
                "Pricing",
                "Competitive Edge",
                "Traction",
                "Team",
                "Close",
            ],
            navigate: "navigate",
            agenda: "agenda",
            exit: "exit",
            agendaHeader: "Agenda",
            agendaTitle: "Jump to any slide",
            agendaTip: "Tip — press 1–9 to jump directly",
            agendaClose: "close",
            slideLabel: "Slide",
        },
        export: {
            title: "Exporting PDF",
            renderingSlide: "Rendering slide",
            of: "of",
            keepOpen: "Capturing all 11 slides — please don't close this tab.",
        },
        langSwitcher: {
            label: "Language",
        },
    },

    es: {
        nav: {
            problem: "Problema",
            solution: "Solución",
            product: "Producto",
            metrics: "Métricas",
            pricing: "Precios",
            team: "Equipo",
            present: "Presentar",
            pdf: "PDF",
            demo: "Demo",
        },
        hero: {
            eyebrow: "Inteligencia táctica para tenis · En vivo en producción",
            headlinePart1: "Supera",
            headlinePart2: "al rival.",
            headlineAccent: "Punto por punto.",
            sub: "Todas las plataformas te muestran qué pasó. Davini convierte los datos del partido en decisiones — qué entrenar, por qué importa y cuánto va a mover tus resultados. Construido sobre la metodología ganadora de Grand Slam de Franco Davín.",
            ctaPrimary: "Solicitar acceso anticipado",
            ctaSecondary: "Ver la plataforma",
            stats: [
                ["8", "Dimensiones analíticas"],
                ["230+", "Métricas de rendimiento / partido"],
                ["1.100+", "Partidos analizados"],
            ],
            semLabel: "S.E.M. — Margen de Efectividad de Saque",
            semSub: "Muestra: juvenil clasificado ITF",
            semCaption: "de puntos de saque ganados (S.E.M.)",
            verdictTitle: "Veredicto del entrenador",
            verdictBody:
                '"Probablemente te quiebren en 1 de cada 3 juegos de servicio con tu nivel actual."',
            verdictBold: "1 de cada 3 juegos de servicio",
            proprietary: "Propietario",
            live: "En vivo",
            legacy: "Trayectoria:",
            legacyBody: "Franco Davín · entrenador de del Potro (US Open 2009) y Gaudio (Roland Garros 2004)",
            marquee: [
                "20+ jugadores profesionales asistidos",
                "Colaboración de investigación con la ITF",
                "Metodología de Grand Slam",
                "Probado en múltiples superficies",
                "250.000+ estadísticas de partido calculadas",
            ],
        },
        problem: {
            tag: "01 — El Problema",
            headline: "El tenis tiene más datos que nunca,",
            headlineAccent: "pero los datos no ganan partidos.",
            sub: "Hawk-Eye, UTR, video tagging — los datos ya existen. Lo que falta es la capa que convierte números en una decisión: qué entrenar ahora, y por qué. Davini nació para ser esa capa.",
            cards: [
                {
                    title: "De los datos a las decisiones",
                    body: "Los números solo importan cuando responden preguntas de entrenador. ¿Qué errores costaron el partido? ¿Dónde se dio vuelta el momentum? ¿En qué largo de peloteo gana tu jugador?",
                    tag: "Capa de decisión",
                },
                {
                    title: "Más allá de la estadística tradicional",
                    body: "Aces y winners cuentan qué pasó. La división deuce/ad, los márgenes de efectividad y los errores determinantes te dicen qué hacer al respecto — la profundidad que define los partidos de élite.",
                    tag: "Profundidad táctica",
                },
                {
                    title: "Inteligencia de rendimiento a largo plazo",
                    body: "Seguí cada métrica a lo largo de 10+ partidos y una temporada completa. El desarrollo deja de ser una intuición y se vuelve una línea de tendencia — con pruebas de que el entrenamiento funciona.",
                    tag: "Visión de temporada",
                },
            ],
        },
        solution: {
            tag: "02 — La Solución",
            headline: "De los datos del partido a tu próxima decisión de entrenamiento en",
            headlineAccent: "3 pasos.",
            steps: [
                {
                    n: "01",
                    title: "Cargar",
                    body: "Importá los datos de tu partido y dejá que Davini los transforme en lecturas tácticas y prioridades de entrenamiento.",
                    meta: "Plantilla CSV · Captura en vivo",
                },
                {
                    n: "02",
                    title: "Analizar",
                    body: "Nuestro motor convierte cada punto en más de 230 métricas de rendimiento en 8 dimensiones — y las destila hasta dejar solo lo que de verdad importa.",
                    meta: "230+ métricas · 8 dimensiones",
                },
                {
                    n: "03",
                    title: "Dominar",
                    body: "No más números para interpretar. Veredictos calibrados por el entrenador, líneas base de 10 partidos y márgenes predictivos te dicen exactamente qué entrenar — y cuánto vale.",
                    meta: "Veredictos del entrenador · Plan de partido",
                },
            ],
            csvLabel: "entrada · partido.csv",
            engine: "Motor Davini →",
        },
        dimensions: {
            tag: "03 — El Producto",
            headline: "8 dimensiones analíticas",
            headlineAccent: "por jugador.",
            sub: "Ocho pestañas dedicadas, cada una responde una pregunta distinta del entrenador — desde quién controló el partido hasta dónde se gana el próximo punto. Además: vistas Flow y Cancha 3D, y un reporte PDF de 12 páginas en tu correo después de cada partido.",
            cards: [
                {
                    code: "RENDIMIENTO",
                    tagline: "Quién controló el partido.",
                    body: "Leadership Margin, rankings de Match Quality, líneas de tendencia en más de 10 rivales, Skillset Radar de 6 ejes.",
                },
                {
                    code: "P1 — ERRORES PRIORITARIOS",
                    tagline: "Los errores que realmente decidieron el partido.",
                    body: "Tasa de Errores No Forzados Determinantes, análisis de Puntos Perdidos en Cadena, detección de patrones consecutivos.",
                },
                {
                    code: "M4 — MINIPARTIDOS",
                    tagline: "% de victoria en 1º y 2º saque y resto.",
                    body: 'Cuatro barras de efectividad con muestra y veredictos calibrados por el entrenador: "Si mantienes estos valores la probabilidad de ganar es baja."',
                },
                {
                    code: "RALLY",
                    tagline: "Dónde el jugador gana o pierde puntos.",
                    body: "Distribución del largo de peloteo en 6 escenarios saque/resto. % de victoria por largo desde 0 hasta 15+ golpes.",
                },
                {
                    code: "SAQUE",
                    tagline: "Una pared completa de gráficos de saque, cada partido.",
                    body: "% de 1º saque dentro, puntos ganados con 1º/2º saque, deuce vs ad, promedios y tendencias.",
                },
                {
                    code: "S.E.M.",
                    tagline: "Margen de Efectividad de Saque.",
                    body: "Métrica compuesta propietaria que predice cada cuánto te van a quebrar el saque. Veredictos calibrados por el entrenador. Seguimiento en el tiempo.",
                },
                {
                    code: "RESTO",
                    tagline: "Refleja la dimensión del saque.",
                    body: "% de 1º/2º resto dentro, efectividad del resto, división deuce/ad — 10 gráficos siguen la fortaleza al resto.",
                },
                {
                    code: "R.E.M.",
                    tagline: "Margen de Efectividad de Resto.",
                    body: "Métrica compuesta propietaria que predice cada cuánto vas a quebrar el saque rival. Complemento del S.E.M. — juntos predicen el resultado.",
                },
            ],
            pills: [
                "Visualización 3D de cancha con filtros de golpes",
                "Match Quality Score",
                "Análisis de break points",
                "Constructor de reportes",
                "Exportación CSV",
                "Filtrado por superficie",
                "Auto-comparación sobre 10 partidos",
                "Reportes PDF automáticos (por correo tras cada partido)",
                "Sincronización automática de rating WTN & UTR",
                "English / Español",
            ],
            proprietary: "Propietario",
            catalogTag: "El catálogo · 24 métricas tácticas",
            catalogTitle: "Todas las métricas que Davini analiza",
            catalogSub: "Cada partido genera este set completo de métricas — en las 8 dimensiones.",
        },
        metrics: {
            tag: "04 — Métricas Propietarias",
            headline: "Métricas creadas para responder una sola pregunta:",
            headlineAccent: "¿qué entrenamos ahora?",
            sub: "Métricas tácticas específicas de tenis construidas sobre el marco de Franco Davín. El análisis de muestra de abajo usa datos reales anonimizados de un juvenil europeo clasificado ITF de 18 años (24+ partidos competitivos seguidos). Mostrado a modo ilustrativo.",
            semVerdict:
                '"Probablemente te quiebren en 1 de cada 3 juegos de saque." El margen de puntos ganados con el saque que predice cada cuánto te van a quebrar.',
            remVerdict:
                '"Alta probabilidad de quebrar al rival en 1 de cada 3 juegos." El margen de puntos ganados al resto que predice cada cuánto vas a quebrar.',
            p1Verdict:
                "El % de errores no forzados que son Determinantes — los errores que realmente decidieron el partido.",
            leadershipLabel: "Leadership Margin",
            leadershipCaption: "10 partidos",
            leadershipPointsLabel: "Puntos controlados",
            leadershipBody:
                "Quién controló el partido punto por punto — seguido en más de 10 rivales.",
            rallyLabel: "Rally Win — bucket más frecuente",
            rallyCaption: "57% @ Rally 1/2",
            rallyBody:
                "El jugador gana la mayor proporción de puntos en peloteos cortos (0–2 golpes) — revela el largo táctico ideal.",
            p1Heading: "P1 — Prioridad 1",
            proprietary: "Propietario",
            simTag: "El Motor de Simulación",
            simTitle: "Simulá la mejora antes de entrenarla.",
            simBody: "Elegí un error prioritario. Bajalo. Davini recalcula Match Performance, S.E.M., R.E.M. y Minipartidos sobre todo el historial — y te muestra cuánto mueve el resultado cada corrección, antes de invertir una sola semana en cancha.",
            simCase: "Caso real: un ex top-40 de la ATP perdía ~12 puntos por partido por lapsos emocionales. La simulación mostró que reducirlos a 7 proyectaba un margen de rendimiento de nivel top-20 — así que el objetivo pasó a ser ganar el punto inmediatamente posterior a cada lapso. La métrica tomó la decisión de entrenamiento.",
            simChip: "Propietario · Análisis what-if",
        },
        dashboard: {
            tag: "05 — Panel de Partido",
            headline: "Cada partido se convierte en un",
            headlineAccent: "reporte táctico.",
            sub: "Replay interactivo, rankings instantáneos, radar de habilidades e historial de golpes — cada partido termina con una lectura clara: qué funcionó, qué no, y qué entrenar esta semana.",
            playerLabel: "Perfil de muestra · juvenil ITF",
            playerSub: "Juvenil europeo ITF · 18 años · Diestro",
            perfTitle: "Rendimiento · Últimos 10",
            perfMargin: "margen deseado: +6",
            skillsetTitle: "Radar de habilidades",
            skillsetSub: "Perfil 6 ejes",
            fhbhTitle: "FH / BH Golpes clave — Histórico",
            minimatchTitle: "Minipartidos — Efectividad",
            qualityTitle: "Top Match Quality",
            featurePills: [
                "Visualización 3D de cancha con filtros de golpes",
                "Match Quality Score",
                "Skillset Radar 6 ejes",
                "Desglose de minipartidos",
                "División Deuce/Ad",
                "Análisis FH / BH",
            ],
            forehand: "Drive",
            backhand: "Revés",
        },
        pricing: {
            tag: "06 — Precios",
            headline: "Planes para jugadores, entrenadores,",
            headlineAccent: "academias y equipos pro.",
            sub: "Precios de acceso anticipado para entrenadores y academias. Davini escala sin hardware, sin instalación, sin niveles ocultos.",
            perMonth: "/mes",
            popular: "Más popular",
            earlyAccess: "Acceso anticipado",
            tiers: [
                {
                    name: "Jugador",
                    price: "$29",
                    tagline:
                        "Para jugadores competitivos que siguen su propio desarrollo.",
                    features: [
                        "Panel personal de analítica",
                        "Hasta 10 partidos / mes",
                        "Las 8 dimensiones analíticas",
                        "Tendencias en los últimos 10 partidos",
                        "Análisis de errores P1",
                        "Skillset radar y largo de peloteo",
                        "Reportes mobile-friendly",
                    ],
                    cta: "Solicitar acceso",
                },
                {
                    name: "Entrenador",
                    price: "$99",
                    tagline:
                        "Para entrenadores privados y viajeros con varios jugadores.",
                    features: [
                        "Todo lo del Jugador",
                        "Partidos ilimitados",
                        "Gestión multi-jugador (hasta 10)",
                        "Métricas S.E.M. / R.E.M. completas",
                        "Análisis de errores P1",
                        "Constructor de reportes",
                        "Soporte prioritario",
                    ],
                    cta: "Solicitar acceso",
                },
                {
                    name: "Academia & Pro",
                    price: "$499",
                    tagline: "Para academias, programas federativos y equipos pro.",
                    features: [
                        "Todo lo del Entrenador",
                        "Perfiles ilimitados",
                        "Analítica de todo el equipo (próximamente)",
                        "Acceso API (próximamente)",
                        "Account manager dedicado",
                        "Reportes white-label (próximamente)",
                    ],
                    cta: "Hablemos",
                },
            ],
        },
        competitive: {
            tag: "07 — Qué Aportamos",
            headline: "Cómo Davini.ai",
            headlineAccent: "es diferente.",
            sub: "Las demás plataformas responden “¿qué pasó?”. Davini responde “¿qué hacemos ahora?” — metodología de élite, inteligencia táctica específica de tenis y veredictos automatizados en un solo flujo. Sin hardware, sin instalación.",
            feature: "Capacidad",
            cols: ["Davini.ai", "Hawk-Eye", "SwingVision", "Stats Manuales"],
            rows: [
                "Análisis punto por punto",
                "8 dimensiones tácticas",
                "Métricas S.E.M. / R.E.M.",
                "Clasificación de errores P1",
                "Tendencias multi-partido (10+)",
                "Veredictos automatizados (calibrados por un entrenador de Grand Slam)",
                "Accesible para entrenadores individuales",
                "Funciona sin hardware",
                "Flujo diseñado por entrenadores",
            ],
            partial: "parcial",
            quote: "Construido con la metodología de",
            quoteAccent: "Franco Davín — entrenador de dos campeones de Grand Slam: Juan Martín del Potro (US Open 2009) y Gastón Gaudio (Roland Garros 2004).",
            quoteRest: "",
            tagline: "Davini.ai — Todas las plataformas te dan datos. Davini te dice qué gana partidos.",
            footnote:
                "Comparación basada en información de producto pública a diciembre de 2025. Los productos competidores siguen evolucionando — verificá antes de comprar.",
        },
        traction: {
            tag: "08 — Tracción",
            headline: "Ya generando",
            headlineAccent: "impacto.",
            stats: [
                ["20+", "Jugadores profesionales asistidos"],
                ["1.100+", "Partidos competitivos analizados"],
                ["230+", "Métricas de rendimiento por partido"],
                ["250.000+", "Estadísticas de partido calculadas"],
            ],
            milestones: [
                "Co-desarrollado con Franco Davín — entrenador de dos campeones de Grand Slam: Juan Martín del Potro (US Open 2009) y Gastón Gaudio (Roland Garros 2004).",
                "Validado con juveniles clasificados ITF en superficies de cemento, polvo de ladrillo e indoor.",
                "Plataforma en producción con las 8 dimensiones analíticas operativas.",
            ],
            trustedTitle: "Confianza en todos los niveles del juego",
            trustedItems: [
                "Profesionales de los tours WTA y ATP — incluidos varios top-20",
                "Un campeón junior de Grand Slam vigente",
                "Juveniles clasificados ITF en tres continentes",
            ],
            orgsNote: "Colaboración de investigación con la Federación Internacional de Tenis (ITF). Utilizado además por equipos nacionales de Copa Davis y Billie Jean King Cup, COSAT (Confederación Sudamericana de Tenis) y academias líderes de toda Sudamérica.",
        },
        team: {
            tag: "09 — El Equipo",
            headline: "Hecho por entrenadores,",
            headlineAccent: "para entrenadores.",
            sub: "Davini.ai nació en las canchas de práctica del tenis de élite. No construimos otra app de stats — construimos la plataforma que deseábamos tener cuando entrenábamos al más alto nivel.",
            members: [
                {
                    name: "Franco Davín",
                    role: "Co-fundador & CEO",
                    sub: "Director de Metodología",
                    bio: "Ex-entrenador de dos campeones de Grand Slam — Juan Martín del Potro (US Open 2009) y Gastón Gaudio (Roland Garros 2004). Décadas de experiencia en el ATP Tour. Arquitecto del marco analítico detrás de cada métrica Davini — P1, S.E.M., R.E.M., Leadership Margin.",
                },
                {
                    name: "Boon Chew",
                    role: "Co-fundador & CTO",
                    sub: "Producto e Ingeniería",
                    bio: "Boon Chew es venture builder y fundador de Tappollo Media y Centaur Lab, enfocado en lanzar y escalar empresas de tecnología e IA. Ayudó a construir las plataformas detrás de startups valuadas en más de 3.000 millones de dólares y trabajó con marcas globales como iHeartRadio, Discovery, CLEAR y SEVENROOMS. Su trayectoria abarca productos de consumo de alto crecimiento y plataformas de impacto social premiadas, incluida la primera app de citas para la comunidad autista y Pepper, una plataforma social con más de 2 millones de descargas.",
                },
            ],
        },
        cta: {
            eyebrow: "¿Listo para entrenar como un profesional?",
            headlinePart1: "Gana el partido",
            headlineAccent: "antes",
            headlinePart2: "de empezar.",
            sub: "Solicitá acceso anticipado. Subí un partido y recibí tu primera lectura calibrada por entrenador — qué entrenar, por qué y cuánto vale.",
            primary: "Solicitar acceso anticipado",
            secondary: "Hablemos",
            footnote:
                "Flujo pensado por entrenadores · Sin hardware · Diseñado sobre la metodología de Franco Davín",
        },
        footer: {
            copyright: "© {year} Davini.ai — Analítica táctica para tenis.",
            product: "Producto",
            pricing: "Precios",
            team: "Equipo",
        },
        presenter: {
            slides: [
                "Portada",
                "Problema",
                "Solución",
                "8 dimensiones",
                "Métricas propietarias",
                "Panel de partido",
                "Precios",
                "Ventaja competitiva",
                "Tracción",
                "Equipo",
                "Cierre",
            ],
            navigate: "navegar",
            agenda: "agenda",
            exit: "salir",
            agendaHeader: "Agenda",
            agendaTitle: "Salta a cualquier diapositiva",
            agendaTip: "Tip — presiona 1–9 para saltar directo",
            agendaClose: "cerrar",
            slideLabel: "Diapositiva",
        },
        export: {
            title: "Exportando PDF",
            renderingSlide: "Renderizando diapositiva",
            of: "de",
            keepOpen: "Capturando las 11 diapositivas — no cierres esta pestaña.",
        },
        langSwitcher: { label: "Idioma" },
    },

    fr: {
        nav: {
            problem: "Problème",
            solution: "Solution",
            product: "Produit",
            metrics: "Métriques",
            pricing: "Tarifs",
            team: "Équipe",
            present: "Présenter",
            pdf: "PDF",
            demo: "Démo",
        },
        hero: {
            eyebrow: "Intelligence tactique pour le tennis · En production",
            headlinePart1: "Devancez",
            headlinePart2: "l'adversaire.",
            headlineAccent: "Point par point.",
            sub: "Toutes les plateformes montrent ce qui s'est passé. Davini transforme les données de match en décisions — quoi entraîner, pourquoi, et combien cela fera bouger vos résultats. Construit sur la méthodologie victorieuse en Grand Chelem de Franco Davín.",
            ctaPrimary: "Demander un accès anticipé",
            ctaSecondary: "Voir la plateforme",
            stats: [
                ["8", "Dimensions analytiques"],
                ["230+", "Métriques de performance / match"],
                ["1 100+", "Matchs analysés"],
            ],
            semLabel: "S.E.M. — Marge d'Efficacité au Service",
            semSub: "Exemple : junior classé ITF",
            semCaption: "des points gagnés au service (S.E.M.)",
            verdictTitle: "Verdict du coach",
            verdictBody:
                '"Vous serez probablement breaké dans 1 jeu de service sur 3 à votre niveau actuel."',
            verdictBold: "1 jeu de service sur 3",
            proprietary: "Propriétaire",
            live: "Direct",
            legacy: "Parcours :",
            legacyBody: "Franco Davín · entraîneur de del Potro (US Open 2009) et Gaudio (Roland-Garros 2004)",
            marquee: [
                "20+ joueurs professionnels accompagnés",
                "Collaboration de recherche avec l'ITF",
                "Méthodologie Grand Chelem",
                "Toutes surfaces testées",
                "250 000+ statistiques de match calculées",
            ],
        },
        problem: {
            tag: "01 — Le Problème",
            headline: "Le tennis n'a jamais eu autant de données,",
            headlineAccent: "mais les données ne gagnent pas les matchs.",
            sub: "Hawk-Eye, UTR, video tagging — les données existent déjà. Ce qui manque, c'est la couche qui transforme les chiffres en décision : quoi entraîner ensuite, et pourquoi. C'est exactement pour cela que Davini existe.",
            cards: [
                {
                    title: "Des données aux décisions",
                    body: "Les chiffres ne comptent que s'ils répondent aux questions du coach. Quelles fautes ont vraiment coûté le match ? Où le momentum a-t-il basculé ? Sur quelle longueur d'échange votre joueur gagne-t-il ?",
                    tag: "Couche de décision",
                },
                {
                    title: "Au-delà des statistiques classiques",
                    body: "Aces et winners racontent ce qui s'est passé. Répartition deuce/ad, marges d'efficacité et fautes déterminantes disent quoi en faire — la profondeur qui décide des matchs de haut niveau.",
                    tag: "Profondeur tactique",
                },
                {
                    title: "Intelligence de performance long terme",
                    body: "Suivez chaque métrique sur 10+ matchs et une saison entière. La progression devient une courbe, pas une intuition — avec la preuve que l'entraînement fonctionne.",
                    tag: "Vision saison",
                },
            ],
        },
        solution: {
            tag: "02 — La Solution",
            headline: "Des données de match à votre prochaine décision d'entraînement en",
            headlineAccent: "3 étapes.",
            steps: [
                {
                    n: "01",
                    title: "Importer",
                    body: "Importez vos données de match et laissez Davini les transformer en lectures tactiques et priorités d'entraînement.",
                    meta: "Modèle CSV · Capture en direct",
                },
                {
                    n: "02",
                    title: "Analyser",
                    body: "Notre moteur transforme chaque point en plus de 230 métriques de performance sur 8 dimensions — puis les distille pour ne garder que ce qui compte vraiment.",
                    meta: "230+ métriques · 8 dimensions",
                },
                {
                    n: "03",
                    title: "Dominer",
                    body: "Pas plus de chiffres à interpréter. Verdicts calibrés par le coach, références sur 10 matchs et marges prédictives disent exactement quoi entraîner — et ce que cela rapporte.",
                    meta: "Verdicts du coach · Plan de match",
                },
            ],
            csvLabel: "entrée · match.csv",
            engine: "Moteur Davini →",
        },
        dimensions: {
            tag: "03 — Le Produit",
            headline: "8 dimensions analytiques",
            headlineAccent: "par joueur.",
            sub: "Huit onglets dédiés, chacun répond à une question de coach — de qui a contrôlé le match à où se gagne le prochain point. Plus les vues Flow et Court 3D, et un rapport PDF de 12 pages livré dans votre boîte mail après chaque match.",
            cards: [
                {
                    code: "PERFORMANCE",
                    tagline: "Qui a contrôlé le match.",
                    body: "Leadership Margin, classements Match Quality, courbes de performance sur 10+ adversaires, Skillset Radar 6 axes.",
                },
                {
                    code: "P1 — FAUTES PRIORITAIRES",
                    tagline: "Les fautes qui ont vraiment décidé du match.",
                    body: "Taux de fautes directes déterminantes, analyse des points perdus consécutifs, détection des séquences.",
                },
                {
                    code: "M4 — MINI-MATCHS",
                    tagline: "% de victoire sur 1er & 2e service et retour.",
                    body: 'Quatre barres d\'efficacité avec échantillon et verdicts calibrés par le coach : "Si vous gardez ces valeurs la probabilité de gagner est faible."',
                },
                {
                    code: "ÉCHANGE",
                    tagline: "Où le joueur gagne ou perd ses points.",
                    body: "Distribution de la longueur d'échange sur 6 scénarios service/retour. % de victoire par segment de 0 à 15+ frappes.",
                },
                {
                    code: "SERVICE",
                    tagline: "Un mur complet de graphiques de service, chaque match.",
                    body: "% de 1er service en jeu, points gagnés au 1er/2e service, deuce vs ad, moyennes et tendances.",
                },
                {
                    code: "S.E.M.",
                    tagline: "Marge d'Efficacité au Service.",
                    body: "Métrique composite propriétaire qui prédit la fréquence à laquelle le service sera breaké. Verdicts calibrés par le coach. Suivi dans le temps.",
                },
                {
                    code: "RETOUR",
                    tagline: "Le miroir du service.",
                    body: "% de 1er/2e retour en jeu, efficacité du retour, répartition deuce/ad — 10 graphiques suivent la force au retour.",
                },
                {
                    code: "R.E.M.",
                    tagline: "Marge d'Efficacité en Retour.",
                    body: "Métrique composite propriétaire qui prédit la fréquence à laquelle le service adverse sera breaké. Complément du S.E.M. — ensemble ils prédisent l'issue du match.",
                },
            ],
            pills: [
                "Visualisation 3D du court avec filtres de coups",
                "Match Quality Score",
                "Analyse des balles de break",
                "Constructeur de rapports",
                "Export CSV",
                "Filtrage par surface",
                "Auto-comparaison sur 10 matchs",
                "Rapports PDF automatiques (envoyés après chaque match)",
                "Synchronisation auto des ratings WTN & UTR",
                "English / Español",
            ],
            proprietary: "Propriétaire",
            catalogTag: "Le catalogue · 24 métriques tactiques",
            catalogTitle: "Toutes les métriques que Davini suit",
            catalogSub: "Chaque match génère cet ensemble complet de métriques — sur les 8 dimensions.",
        },
        metrics: {
            tag: "04 — Métriques Propriétaires",
            headline: "Des métriques conçues pour répondre à une seule question :",
            headlineAccent: "on entraîne quoi ensuite ?",
            sub: "Des métriques tactiques spécifiques au tennis, construites sur le cadre de coaching de Franco Davín. L'analyse ci-dessous utilise des données réelles anonymisées d'un junior européen classé ITF de 18 ans (24+ matchs compétitifs suivis). Présentée à titre d'illustration.",
            semVerdict:
                '"Le service sera probablement breaké dans 1 jeu sur 3." La marge de points gagnés au service qui prédit la fréquence des breaks subis.',
            remVerdict:
                '"Forte probabilité de breaker l\'adversaire dans 1 jeu sur 3." La marge de points gagnés en retour qui prédit la fréquence de vos breaks.',
            p1Verdict:
                "Le % de fautes directes Déterminantes — celles qui ont vraiment décidé du match.",
            leadershipLabel: "Leadership Margin",
            leadershipCaption: "10 matchs",
            leadershipPointsLabel: "Points contrôlés",
            leadershipBody:
                "Qui a contrôlé le match point par point — suivi sur 10+ adversaires.",
            rallyLabel: "Rally Win — segment le plus fréquent",
            rallyCaption: "57% gagnés @ 0–2 frappes",
            rallyBody:
                "57% des points gagnés sur la longueur d'échange la plus fréquente (0–2 frappes) — révèle la longueur tactique idéale.",
            p1Heading: "P1 — Priorité 1",
            proprietary: "Propriétaire",
            simTag: "Le Moteur de Simulation",
            simTitle: "Simulez le progrès avant de l'entraîner.",
            simBody: "Choisissez une faute prioritaire. Réduisez-la. Davini recalcule Match Performance, S.E.M., R.E.M. et Mini-matchs sur tout l'historique — et montre combien chaque correction fait bouger les résultats, avant de passer une seule semaine sur le court.",
            simCase: "Cas réel : un ancien top 40 ATP perdait ~12 points par match sur des baisses émotionnelles. La simulation a montré qu'en les ramenant à 7, il projetait une marge de performance de niveau top 20 — l'objectif de coaching est donc devenu : gagner le point qui suit chaque baisse. C'est la métrique qui a dicté le choix de coaching.",
            simChip: "Propriétaire · Analyse what-if",
        },
        dashboard: {
            tag: "05 — Tableau de Match",
            headline: "Chaque match devient un",
            headlineAccent: "rapport tactique.",
            sub: "Replay interactif, classements instantanés, radar de compétences et historique des coups — chaque match se termine par une lecture claire : ce qui a marché, ce qui n'a pas marché, et quoi entraîner cette semaine.",
            playerLabel: "Profil d'exemple · junior ITF",
            playerSub: "Junior européen ITF · 18 ans · Droitier",
            perfTitle: "Performance · 10 derniers",
            perfMargin: "marge souhaitée : +6",
            skillsetTitle: "Radar de compétences",
            skillsetSub: "Profil 6 axes",
            fhbhTitle: "FH / BH Coups clés — Historique",
            minimatchTitle: "Mini-matchs — Efficacité",
            qualityTitle: "Top Match Quality",
            featurePills: [
                "Visualisation 3D du court avec filtres de coups",
                "Match Quality Score",
                "Skillset Radar 6 axes",
                "Décomposition des mini-matchs",
                "Répartition Deuce/Ad",
                "Analyse FH / BH",
            ],
            forehand: "Coup droit",
            backhand: "Revers",
        },
        pricing: {
            tag: "06 — Tarifs",
            headline: "Des plans pour joueurs, entraîneurs,",
            headlineAccent: "académies et équipes pro.",
            sub: "Tarifs d'accès anticipé pour entraîneurs et académies. Davini évolue sans matériel, sans frais d'installation, sans paliers cachés.",
            perMonth: "/mois",
            popular: "Le plus populaire",
            earlyAccess: "Accès anticipé",
            tiers: [
                {
                    name: "Joueur",
                    price: "29 $",
                    tagline:
                        "Pour les joueurs de compétition qui suivent leur progression.",
                    features: [
                        "Tableau analytique personnel",
                        "Jusqu'à 10 matchs / mois",
                        "Les 8 dimensions analytiques",
                        "Tendances sur les 10 derniers matchs",
                        "Analyse des fautes P1",
                        "Radar de compétences & longueur d'échange",
                        "Rapports mobile-friendly",
                    ],
                    cta: "Demander un accès",
                },
                {
                    name: "Entraîneur",
                    price: "99 $",
                    tagline:
                        "Pour les entraîneurs privés et itinérants gérant plusieurs joueurs.",
                    features: [
                        "Tout du Joueur",
                        "Matchs illimités",
                        "Gestion multi-joueurs (jusqu'à 10)",
                        "Métriques S.E.M. / R.E.M. complètes",
                        "Analyse des fautes P1",
                        "Constructeur de rapports",
                        "Support prioritaire",
                    ],
                    cta: "Demander un accès",
                },
                {
                    name: "Académie & Pro",
                    price: "499 $",
                    tagline:
                        "Pour les académies, programmes fédéraux et équipes pro.",
                    features: [
                        "Tout de l'Entraîneur",
                        "Profils illimités",
                        "Analytique d'équipe (bientôt disponible)",
                        "Accès API (bientôt disponible)",
                        "Account manager dédié",
                        "Rapports white-label (bientôt disponible)",
                    ],
                    cta: "Discutons",
                },
            ],
        },
        competitive: {
            tag: "07 — Ce Que Nous Apportons",
            headline: "Comment Davini.ai",
            headlineAccent: "se distingue.",
            sub: "Les autres plateformes répondent « que s'est-il passé ? ». Davini répond « que fait-on maintenant ? » — méthodologie d'élite, intelligence tactique dédiée au tennis et verdicts automatisés dans un seul flux. Sans matériel, sans installation.",
            feature: "Capacité",
            cols: ["Davini.ai", "Hawk-Eye", "SwingVision", "Stats Manuelles"],
            rows: [
                "Analyse point par point",
                "8 dimensions tactiques",
                "Métriques S.E.M. / R.E.M.",
                "Classification fautes P1",
                "Tendances multi-matchs (10+)",
                "Verdicts automatisés (calibrés par un coach de Grand Chelem)",
                "Accessible aux entraîneurs indépendants",
                "Fonctionne sans matériel",
                "Workflow conçu par entraîneurs",
            ],
            partial: "partiel",
            quote: "Construit avec la méthodologie de coaching de",
            quoteAccent: "Franco Davín — entraîneur de deux champions de Grand Chelem : Juan Martín del Potro (US Open 2009) et Gastón Gaudio (Roland-Garros 2004).",
            quoteRest: "",
            tagline: "Davini.ai — Toutes les plateformes donnent des données. Davini dit ce qui gagne des matchs.",
            footnote:
                "Comparaison basée sur les informations produit publiques en décembre 2025. Les produits concurrents évoluent — vérifiez avant tout achat.",
        },
        traction: {
            tag: "08 — Traction",
            headline: "Déjà un",
            headlineAccent: "impact réel.",
            stats: [
                ["20+", "Joueurs professionnels accompagnés"],
                ["1 100+", "Matchs compétitifs analysés"],
                ["230+", "Métriques de performance par match"],
                ["250 000+", "Statistiques de match calculées"],
            ],
            milestones: [
                "Co-développé avec Franco Davín — ancien entraîneur de deux champions de Grand Chelem : Juan Martín del Potro (US Open 2009) et Gastón Gaudio (Roland-Garros 2004).",
                "Validé avec des juniors classés ITF sur dur, terre battue et indoor.",
                "Plateforme déployée en production avec les 8 dimensions opérationnelles.",
            ],
            trustedTitle: "La confiance à tous les niveaux du jeu",
            trustedItems: [
                "Professionnels des circuits WTA & ATP — dont plusieurs top-20",
                "Un champion junior de Grand Chelem en titre",
                "Juniors classés ITF sur trois continents",
            ],
            orgsNote: "Collaboration de recherche avec la Fédération Internationale de Tennis (ITF). Également utilisé par des équipes nationales de Coupe Davis et de Billie Jean King Cup, la COSAT (Confédération Sud-Américaine de Tennis) et de grandes académies d'Amérique du Sud.",
        },
        team: {
            tag: "09 — L'Équipe",
            headline: "Conçu par des entraîneurs,",
            headlineAccent: "pour des entraîneurs.",
            sub: "Davini.ai est né sur les courts d'entraînement du tennis d'élite. Nous n'avons pas construit une énième app de stats — nous avons construit la plateforme analytique que nous aurions voulue au plus haut niveau.",
            members: [
                {
                    name: "Franco Davín",
                    role: "Co-fondateur & CEO",
                    sub: "Direction Méthodologie",
                    bio: "Ancien entraîneur de deux champions de Grand Chelem — Juan Martín del Potro (US Open 2009) et Gastón Gaudio (Roland-Garros 2004). Décennies d'expérience sur l'ATP Tour. Architecte du cadre analytique derrière chaque métrique Davini — P1, S.E.M., R.E.M., Leadership Margin.",
                },
                {
                    name: "Boon Chew",
                    role: "Co-fondateur & CTO",
                    sub: "Produit & Ingénierie",
                    bio: "Boon Chew est venture builder et fondateur de Tappollo Media et Centaur Lab, dédié au lancement et à la mise à l'échelle d'entreprises tech et IA. Il a contribué à créer les plateformes derrière des startups valorisées à plus de 3 milliards de dollars et collaboré avec des marques mondiales comme iHeartRadio, Discovery, CLEAR et SEVENROOMS. Son travail couvre des produits grand public à forte croissance et des plateformes d'impact social primées, dont la première app de rencontres pour la communauté autiste et Pepper, plateforme sociale aux plus de 2 millions de téléchargements.",
                },
            ],
        },
        cta: {
            eyebrow: "Prêt à coacher comme un pro ?",
            headlinePart1: "Gagnez le match",
            headlineAccent: "avant",
            headlinePart2: "qu'il ne commence.",
            sub: "Demandez un accès anticipé. Importez un match et recevez votre première lecture calibrée par un coach — quoi entraîner, pourquoi, et ce que cela vaut.",
            primary: "Demander un accès anticipé",
            secondary: "Discutons",
            footnote:
                "Workflow pensé par les coachs · Sans matériel · Conçu autour de la méthodologie de Franco Davín",
        },
        footer: {
            copyright: "© {year} Davini.ai — Analyse tactique tennis.",
            product: "Produit",
            pricing: "Tarifs",
            team: "Équipe",
        },
        presenter: {
            slides: [
                "Couverture",
                "Problème",
                "Solution",
                "8 dimensions",
                "Métriques propriétaires",
                "Tableau de match",
                "Tarifs",
                "Avantage compétitif",
                "Traction",
                "Équipe",
                "Clôture",
            ],
            navigate: "naviguer",
            agenda: "agenda",
            exit: "quitter",
            agendaHeader: "Agenda",
            agendaTitle: "Aller à une diapositive",
            agendaTip: "Astuce — 1–9 pour sauter directement",
            agendaClose: "fermer",
            slideLabel: "Diapositive",
        },
        export: {
            title: "Export PDF",
            renderingSlide: "Rendu de la diapositive",
            of: "sur",
            keepOpen:
                "Capture des 11 diapositives — ne fermez pas cet onglet.",
        },
        langSwitcher: { label: "Langue" },
    },

    zh: {
        nav: {
            problem: "问题",
            solution: "方案",
            product: "产品",
            metrics: "指标",
            pricing: "价格",
            team: "团队",
            present: "演示",
            pdf: "PDF",
            demo: "试用",
        },
        hero: {
            eyebrow: "网球战术智能 · 已上线运行",
            headlinePart1: "智胜",
            headlinePart2: "对手。",
            headlineAccent: "分分必争。",
            sub: "所有平台都在告诉你发生了什么。Davini 把比赛数据变成决策 — 练什么、为什么练、能带来多大改变。背后是 Franco Davín（弗朗哥·达文）赢下大满贯的执教方法论。",
            ctaPrimary: "申请抢先体验",
            ctaSecondary: "查看平台",
            stats: [
                ["8", "分析维度"],
                ["230+", "每场比赛的表现指标"],
                ["1,100+", "已分析的正式比赛"],
            ],
            semLabel: "S.E.M. — 发球效能净值",
            semSub: "示例：ITF 注册青少年",
            semCaption: "发球分得分率（S.E.M.）",
            verdictTitle: "教练判读",
            verdictBody:
                '"以你当前水平，每 3 个发球局大约会被破发 1 次。"',
            verdictBold: "每 3 个发球局 1 次",
            proprietary: "自有指标",
            live: "实时",
            legacy: "执教履历：",
            legacyBody:
                "Franco Davín · 德尔波特罗（2009 美网）与高迪奥（2004 法网）的前主教练",
            marquee: [
                "服务 20+ 位职业球员",
                "与 ITF 开展研究合作",
                "大满贯执教方法论",
                "多场地验证",
                "250,000+ 项比赛统计数据",
            ],
        },
        problem: {
            tag: "01 — 问题",
            headline: "网球的数据空前丰富，",
            headlineAccent: "但数据本身赢不了比赛。",
            sub: "鹰眼、UTR、视频打标 — 数据早就存在。缺的是把数字变成决策的那一层：下一步练什么、为什么练。Davini 就是为这一层而生。",
            cards: [
                {
                    title: "从数据到决策",
                    body: "数字只有在回答教练的问题时才有价值。哪些失误真正输掉了比赛？势头在哪一刻翻转？你的球员在多少拍的相持中赢分？",
                    tag: "决策层",
                },
                {
                    title: "超越传统统计",
                    body: "ACE 与制胜分告诉你发生了什么。平分区 / 占先区分布、效能净值与决定性失误告诉你该怎么办 — 这才是决定顶级比赛的深度。",
                    tag: "战术深度",
                },
                {
                    title: "长期表现智能",
                    body: "跨 10+ 场比赛、贯穿整个赛季追踪每一项指标。球员发展从直觉变成一条趋势线 — 并且能证明训练确实有效。",
                    tag: "赛季视角",
                },
            ],
        },
        solution: {
            tag: "02 — 方案",
            headline: "从原始比赛数据到下一个训练决策，",
            headlineAccent: "只需三步。",
            steps: [
                {
                    n: "01",
                    title: "上传",
                    body: "导入你的比赛数据，让 Davini 把它转化为战术洞察与训练优先级。",
                    meta: "CSV 模板 · 实时记录",
                },
                {
                    n: "02",
                    title: "分析",
                    body: "我们的引擎把每一分拆解成 230+ 项表现指标，覆盖 8 个分析维度 — 再层层提炼，只留下真正重要的东西。",
                    meta: "230+ 指标 · 8 维分析",
                },
                {
                    n: "03",
                    title: "制胜",
                    body: "不再是一堆待解读的数字。教练校准的判读、10 场基线与预测性净值，直接告诉你下一步练什么 — 以及它值多少。",
                    meta: "教练判读 · 比赛方案",
                },
            ],
            csvLabel: "输入 · match.csv",
            engine: "Davini 引擎 →",
        },
        dimensions: {
            tag: "03 — 产品",
            headline: "每位球员",
            headlineAccent: "8 大分析维度。",
            sub: "8 个专属页签，每一个回答教练的一个问题 — 从谁掌控了比赛，到下一分在哪里赢。此外还有 Flow 视图与 3D 场地视图，以及每场比赛后自动发送到邮箱的 12 页 PDF 报告。",
            cards: [
                {
                    code: "综合表现 PERFORMANCE",
                    tagline: "谁主导了这场比赛。",
                    body: "Leadership Margin、Match Quality 排名、跨 10+ 对手的趋势曲线、六维技术雷达（Skillset Radar）。",
                },
                {
                    code: "P1 — 关键失误",
                    tagline: "真正输掉比赛的失误。",
                    body: "决定性非受迫失误率、连续丢分（Lost Points in a Row）分析、连续失误模式识别。",
                },
                {
                    code: "M4 — 小比分（MINIMATCHES）",
                    tagline: "一发、二发、接发的胜率拆解。",
                    body: '四组带样本量的有效性条形图，附教练校准的判读："如果维持这些数值，赢下比赛的概率偏低。"',
                },
                {
                    code: "多拍 RALLY",
                    tagline: "球员在哪里赢分、在哪里失分。",
                    body: "覆盖 6 种发球 / 接发球情境的多拍长度分布。从 0 拍到 15+ 拍每个区间的胜率。",
                },
                {
                    code: "发球 SERVE",
                    tagline: "每一场比赛，一整面发球图表墙。",
                    body: "一发进区率、一发 / 二发得分率、平分区 vs 占先区分布、滑动平均与趋势。",
                },
                {
                    code: "S.E.M.",
                    tagline: "发球效能净值（Serve Effectiveness Margin）。",
                    body: "自有合成指标，预测发球局被破发的频率。教练校准的判读，并跨时间追踪，衡量进步。",
                },
                {
                    code: "接发球 RETURN",
                    tagline: "与发球维度对称。",
                    body: "一发 / 二发接发进区率、接发有效性、平分区 / 占先区分布 — 10 张图表追踪接发球强度。",
                },
                {
                    code: "R.E.M.",
                    tagline: "接发效能净值（Return Effectiveness Margin）。",
                    body: "自有合成指标，预测破掉对手发球局的频率。S.E.M. 的镜像 — 两者合起来即可预测比赛走向。",
                },
            ],
            pills: [
                "3D 场地可视化（支持击球筛选）",
                "Match Quality Score",
                "破发点分析",
                "报告构建器",
                "CSV 导出",
                "多场地筛选",
                "10 场自我基准对比",
                "自动 PDF 比赛报告（每场赛后邮件送达）",
                "WTN & UTR 等级分自动同步",
                "English / Español",
            ],
            proprietary: "自有指标",
            catalogTag: "指标目录 · 24 项战术指标",
            catalogTitle: "Davini 追踪的全部指标",
            catalogSub: "每一场比赛都会生成这一整套指标 — 覆盖全部 8 个维度。",
        },
        metrics: {
            tag: "04 — 自有指标",
            headline: "所有指标只为回答一个问题：",
            headlineAccent: "下一步练什么？",
            sub: "围绕 Franco Davín 执教框架打造的网球专属战术指标。下方示例分析使用一位 18 岁的欧洲 ITF 注册青少年（已追踪 24+ 场正式比赛）的真实匿名数据，仅供示意。",
            semVerdict:
                '"按当前水平，每 3 个发球局大约会被破发 1 次。"以发球分得分率预测被破发的频率。',
            remVerdict:
                '"在每 3 个对手发球局中，存在较高的破发概率。"以接发分得分率预测破发的频率。',
            p1Verdict:
                "非受迫失误中属于「决定性失误」的比例 — 真正左右比赛胜负的那一类失误。",
            leadershipLabel: "Leadership Margin",
            leadershipCaption: "10 场",
            leadershipPointsLabel: "掌控分数",
            leadershipBody:
                "球员是否在一分一分中真正主导比赛 — 跨 10+ 对手持续追踪。",
            rallyLabel: "Rally Win — 最高频多拍区间",
            rallyCaption: "57% 胜率 @ 0–2 拍",
            rallyBody:
                "在最高频的多拍长度（0–2 拍）赢下 57% 的分 — 揭示球员理想的战术节奏。",
            p1Heading: "P1 — Priority 1",
            proprietary: "自有指标",
            simTag: "模拟引擎",
            simTitle: "在训练之前，先模拟提升的效果。",
            simBody: "选定一个关键失误，把它调低。Davini 基于整个比赛历史重新计算 Match Performance、S.E.M.、R.E.M. 与小比分 — 在你投入一周训练之前，先看清每一项修正能带来多大改变。",
            simCase: "真实案例：一位前 ATP Top 40 球员每场因情绪波动损失约 12 分。模拟显示，把它降到 7 分即可达到 Top 20 级别的表现净值 — 于是训练目标变成赢下每次情绪波动后的那一分。是指标做出了这次执教决策。",
            simChip: "自有功能 · What-if 分析",
        },
        dashboard: {
            tag: "05 — 比赛仪表板",
            headline: "每一场比赛都会成为",
            headlineAccent: "一份战术报告。",
            sub: "交互式回放、即时排名、技术雷达与击球历史 — 每场比赛结束时都有一份清晰的结论：什么奏效、什么没有、这周练什么。",
            playerLabel: "示例档案 · ITF 青少年",
            playerSub: "欧洲 ITF 青少年 · 18 岁 · 右手持拍",
            perfTitle: "比赛表现 · 最近 10 场",
            perfMargin: "目标净值：+6",
            skillsetTitle: "技术雷达",
            skillsetSub: "六轴档案",
            fhbhTitle: "正手 / 反手关键击球 — 历史",
            minimatchTitle: "小比分 — 有效性",
            qualityTitle: "Top Match Quality",
            featurePills: [
                "3D 场地可视化（支持击球筛选）",
                "Match Quality Score",
                "六轴技术雷达",
                "小比分拆解",
                "平分区 / 占先区分布",
                "正手 / 反手击球分析",
            ],
            forehand: "正手",
            backhand: "反手",
        },
        pricing: {
            tag: "06 — 价格",
            headline: "面向球员、教练、",
            headlineAccent: "学院与职业战队。",
            sub: "面向教练与学院的抢先体验定价。Davini 无需硬件成本、安装费，也没有隐藏阶梯。",
            perMonth: "/月",
            popular: "最受欢迎",
            earlyAccess: "抢先体验",
            tiers: [
                {
                    name: "球员",
                    price: "$29",
                    tagline: "适合追踪自身发展的竞技球员。",
                    features: [
                        "个人比赛分析仪表板",
                        "每月最多 10 场比赛",
                        "全部 8 个分析维度",
                        "近 10 场表现趋势",
                        "P1 失误分析",
                        "技术雷达与多拍长度图表",
                        "移动端友好的报告",
                    ],
                    cta: "申请访问",
                },
                {
                    name: "教练",
                    price: "$99",
                    tagline: "适合管理多名球员的私人教练与巡回教练。",
                    features: [
                        "包含球员套餐的全部功能",
                        "无限场次",
                        "多球员管理（最多 10 位）",
                        "完整 S.E.M. / R.E.M. 指标",
                        "P1 失误分析",
                        "自定义报告构建器",
                        "优先支持",
                    ],
                    cta: "申请访问",
                },
                {
                    name: "学院 & 职业战队",
                    price: "$499",
                    tagline: "适合网球学院、协会项目与职业战队。",
                    features: [
                        "包含教练套餐的全部功能",
                        "无限球员档案",
                        "全队分析与基准对标（即将推出）",
                        "API 接入，支持自定义集成（即将推出）",
                        "专属客户经理",
                        "白标报告（即将推出）",
                    ],
                    cta: "联系我们",
                },
            ],
        },
        competitive: {
            tag: "07 — 我们带来什么",
            headline: "Davini.ai",
            headlineAccent: "为什么不一样。",
            sub: "其他平台回答「发生了什么？」，Davini 回答「接下来怎么办？」— 顶级方法论、网球专属战术智能与自动化教练判读，整合在一个工作流中。无需硬件，无需安装。",
            feature: "能力",
            cols: ["Davini.ai", "Hawk-Eye", "SwingVision", "人工统计"],
            rows: [
                "一分一分级分析",
                "8 大战术分析维度",
                "S.E.M. / R.E.M. 指标",
                "P1 失误分类",
                "多场次趋势（10+）",
                "自动化教练判读（由大满贯教练校准）",
                "个人教练即可使用",
                "无需硬件即可运行",
                "由教练设计的工作流",
            ],
            partial: "部分",
            quote: "构建于",
            quoteAccent:
                "Franco Davín 的执教方法论之上 — 两位大满贯冠军的主教练：胡安·马丁·德尔波特罗（2009 美网）与加斯顿·高迪奥（2004 法网）。",
            quoteRest: "",
            tagline: "Davini.ai — 所有平台给你数据，Davini 告诉你怎么赢。",
            footnote:
                "对比基于截至 2025 年 12 月公开的产品信息。竞争产品仍在迭代，购买前请自行核验。",
        },
        traction: {
            tag: "08 — 进展",
            headline: "已经在",
            headlineAccent: "产生影响。",
            stats: [
                ["20+", "服务的职业球员"],
                ["1,100+", "已分析的正式比赛"],
                ["230+", "每场比赛的表现指标"],
                ["250,000+", "已计算的比赛统计数据"],
            ],
            milestones: [
                "与 Franco Davín 共同打造 — 胡安·马丁·德尔波特罗（2009 美网冠军）与加斯顿·高迪奥（2004 法网冠军）的前主教练。",
                "在硬地、红土与室内场地上由 ITF 注册青少年完成验证。",
                "平台已投入生产环境，8 大分析维度全部可用。",
            ],
            trustedTitle: "深受各级别球员信赖",
            trustedItems: [
                "WTA 与 ATP 巡回赛职业球员 — 包括多位 Top 20",
                "一位现任青少年大满贯冠军",
                "横跨三大洲的 ITF 注册青少年",
            ],
            orgsNote: "与国际网球联合会（ITF）开展研究合作。同时服务于戴维斯杯与比利·简·金杯国家队、南美网球联合会（COSAT）以及南美各地领先的网球学院。",
        },
        team: {
            tag: "09 — 团队",
            headline: "由教练打造，",
            headlineAccent: "服务教练。",
            sub: "Davini.ai 诞生于顶级网球的训练场。我们不是再造一款 stats App — 我们打造的是当年在最高水平执教时就希望存在的分析平台。",
            members: [
                {
                    name: "Franco Davín",
                    role: "联合创始人兼 CEO",
                    sub: "执教方法论负责人",
                    bio: "两位大满贯冠军的前主教练 — 胡安·马丁·德尔波特罗（2009 美网）与加斯顿·高迪奥（2004 法网）。在 ATP 巡回赛积累数十年执教经验。Davini 每一项指标背后分析框架的设计者 — P1、S.E.M.、R.E.M.、Leadership Margin。",
                },
                {
                    name: "Boon Chew",
                    role: "联合创始人兼 CTO",
                    sub: "产品与工程",
                    bio: "Boon Chew 是一位创业孵化者，也是 Tappollo Media 与 Centaur Lab 的创始人，专注于科技与 AI 公司的从 0 到 1 与规模化。他参与打造了估值合计超过 30 亿美元的初创公司背后的平台，并与 iHeartRadio、Discovery、CLEAR、SEVENROOMS 等全球品牌合作。他的作品横跨高速增长的消费产品与屡获殊荣的社会影响力平台，包括首款面向自闭症社群的约会应用，以及下载量超过 200 万的社交平台 Pepper。",
                },
            ],
        },
        cta: {
            eyebrow: "准备好像职业球员一样训练了吗？",
            headlinePart1: "在比赛开始前，",
            headlineAccent: "提前",
            headlinePart2: "赢下它。",
            sub: "申请抢先体验。上传一场比赛，获得第一份由教练校准的结论 — 练什么、为什么、值多少。",
            primary: "申请抢先体验",
            secondary: "联系我们",
            footnote:
                "以教练为先的工作流 · 无需硬件 · 围绕 Franco Davín 的方法论设计",
        },
        footer: {
            copyright: "© {year} Davini.ai — 网球的战术分析。",
            product: "产品",
            pricing: "价格",
            team: "团队",
        },
        presenter: {
            slides: [
                "封面",
                "问题",
                "方案",
                "8 个维度",
                "自有指标",
                "比赛仪表板",
                "价格",
                "竞争优势",
                "进展",
                "团队",
                "结语",
            ],
            navigate: "切换",
            agenda: "目录",
            exit: "退出",
            agendaHeader: "目录",
            agendaTitle: "跳转到任意幻灯片",
            agendaTip: "小贴士 — 按 1–9 直接跳转",
            agendaClose: "关闭",
            slideLabel: "第",
        },
        export: {
            title: "正在导出 PDF",
            renderingSlide: "正在渲染幻灯片",
            of: "/",
            keepOpen: "正在捕获 11 张幻灯片 — 请不要关闭此标签页。",
        },
        langSwitcher: { label: "语言" },
    },

    pl: {
        nav: {
            problem: "Problem",
            solution: "Rozwiązanie",
            product: "Produkt",
            metrics: "Metryki",
            pricing: "Cennik",
            team: "Zespół",
            present: "Prezentuj",
            pdf: "PDF",
            demo: "Demo",
        },
        hero: {
            eyebrow: "Inteligencja taktyczna dla tenisa · Działa produkcyjnie",
            headlinePart1: "Przechytrz",
            headlinePart2: "przeciwnika.",
            headlineAccent: "Punkt po punkcie.",
            sub: "Każda platforma pokazuje, co się wydarzyło. Davini zamienia dane meczowe w decyzje — co trenować, dlaczego to ważne i jak bardzo przesunie to wyniki. Zbudowane na wielkoszlemowej metodologii Franco Davína.",
            ctaPrimary: "Poproś o wczesny dostęp",
            ctaSecondary: "Zobacz platformę",
            stats: [
                ["8", "Wymiarów analitycznych"],
                ["230+", "Metryk wydajności / mecz"],
                ["1 100+", "Przeanalizowanych meczów"],
            ],
            semLabel: "S.E.M. — Serve Effectiveness Margin",
            semSub: "Próbka: junior klasyfikowany przez ITF",
            semCaption: "wygranych punktów serwisowych (S.E.M.)",
            verdictTitle: "Werdykt trenera",
            verdictBody:
                '"Na obecnym poziomie prawdopodobnie stracisz podanie w 1 na 3 gemy serwisowe."',
            verdictBold: "1 na 3 gemy serwisowe",
            proprietary: "Autorska",
            live: "Na żywo",
            legacy: "Dziedzictwo trenerskie:",
            legacyBody: "Franco Davín · trener del Potro (US Open 2009) i Gaudio (Roland Garros 2004)",
            marquee: [
                "20+ wspieranych zawodowych graczy",
                "Współpraca badawcza z ITF",
                "Wielkoszlemowa metodologia trenerska",
                "Testowane na różnych nawierzchniach",
                "250 000+ obliczonych statystyk meczowych",
            ],
        },
        problem: {
            tag: "01 — Problem",
            headline: "Tenis ma więcej danych niż kiedykolwiek,",
            headlineAccent: "ale to nie dane wygrywają mecze.",
            sub: "Hawk-Eye, UTR, tagowanie wideo — dane już istnieją. Brakuje warstwy, która zamienia liczby w decyzję: co trenować dalej i dlaczego. Davini powstało właśnie po to.",
            cards: [
                {
                    title: "Od danych do decyzji",
                    body: "Liczby mają wartość tylko wtedy, gdy odpowiadają na pytania trenera. Które błędy naprawdę kosztowały mecz? Gdzie odwróciło się momentum? Przy jakiej długości wymiany Twój zawodnik wygrywa?",
                    tag: "Warstwa decyzji",
                },
                {
                    title: "Dalej niż tradycyjne statystyki",
                    body: "Asy i winnery mówią, co się stało. Podział na stronę równą i przewag, marginesy skuteczności i błędy decydujące mówią, co z tym zrobić — to głębia, która rozstrzyga mecze na najwyższym poziomie.",
                    tag: "Głębia taktyczna",
                },
                {
                    title: "Długoterminowa inteligencja wyników",
                    body: "Śledź każdą metrykę w 10+ meczach i przez cały sezon. Rozwój przestaje być intuicją, a staje się linią trendu — z dowodem, że trening działa.",
                    tag: "Perspektywa sezonu",
                },
            ],
        },
        solution: {
            tag: "02 — Rozwiązanie",
            headline: "Od surowych danych meczowych do następnej decyzji treningowej w",
            headlineAccent: "3 krokach.",
            steps: [
                {
                    n: "01",
                    title: "Wgraj",
                    body: "Zaimportuj dane meczowe i pozwól, aby Davini zamieniło je w taktyczne wnioski i priorytety treningowe.",
                    meta: "Szablon CSV · Zapis na żywo",
                },
                {
                    n: "02",
                    title: "Analizuj",
                    body: "Nasz silnik zamienia każdy punkt w 230+ metryk wydajności w 8 wymiarach analitycznych — a potem destyluje je do tego, co naprawdę ma znaczenie.",
                    meta: "230+ metryk · 8 wymiarów",
                },
                {
                    n: "03",
                    title: "Dominuj",
                    body: "Koniec z interpretowaniem liczb. Werdykty skalibrowane przez trenera, bazy z 10 meczów i predykcyjne marginesy mówią dokładnie, co trenować dalej — i ile to jest warte.",
                    meta: "Werdykty trenera · Plany meczowe",
                },
            ],
            csvLabel: "wejście · match.csv",
            engine: "Silnik Davini →",
        },
        dimensions: {
            tag: "03 — Produkt",
            headline: "8 wymiarów analitycznych",
            headlineAccent: "na zawodnika.",
            sub: "Osiem dedykowanych zakładek — każda odpowiada na inne pytanie trenera: od tego, kto kontrolował mecz, po to, gdzie wygrywa się następny punkt. Do tego widoki Flow i kort 3D oraz 12-stronicowy raport PDF trafiający na Twoją skrzynkę po każdym meczu.",
            cards: [
                {
                    code: "PERFORMANCE",
                    tagline: "Kto kontrolował mecz.",
                    body: "Leadership Margin, rankingi Match Quality, linie trendów w 10+ meczach z rywalami, 6-osiowy Skillset Radar.",
                },
                {
                    code: "P1 — PRIORITY ERRORS",
                    tagline: "Błędy, które naprawdę zdecydowały o meczu.",
                    body: "Wskaźnik decydujących błędów niewymuszonych, analiza punktów przegranych z rzędu, wykrywanie wzorców błędów seryjnych.",
                },
                {
                    code: "M4 — MINIMATCHES",
                    tagline: "% wygranych przy 1. i 2. serwisie oraz returnie.",
                    body: 'Cztery paski skuteczności z wielkością próby i werdyktami skalibrowanymi przez trenera: "Jeśli utrzymasz te wartości, prawdopodobieństwo wygrywania meczów jest niskie."',
                },
                {
                    code: "RALLY",
                    tagline: "Gdzie zawodnik wygrywa lub traci punkty.",
                    body: "Rozkład długości wymian w 6 scenariuszach serwis/return. % wygranych w każdym przedziale wymiany od 0 do 15+ uderzeń.",
                },
                {
                    code: "SERVE",
                    tagline: "Cała ściana wykresów serwisowych, każdy mecz.",
                    body: "% trafionych 1. serwisów, punkty wygrane po 1./2. serwisie, podział na stronę równą i przewag, średnie kroczące i trendy.",
                },
                {
                    code: "S.E.M.",
                    tagline: "Serve Effectiveness Margin.",
                    body: "Autorska metryka złożona przewidująca, jak często Twój serwis będzie przełamywany. Werdykty skalibrowane przez trenera. Śledzona w czasie, aby mierzyć postępy.",
                },
                {
                    code: "RETURN",
                    tagline: "Lustrzane odbicie wymiaru serwisu.",
                    body: "% trafionych returnów po 1./2. serwisie, skuteczność returnu, podział na stronę równą i przewag — 10 wykresów śledzących siłę returnu.",
                },
                {
                    code: "R.E.M.",
                    tagline: "Return Effectiveness Margin.",
                    body: "Autorska metryka złożona przewidująca, jak często przełamiesz serwis rywala. Dopełnienie S.E.M. — razem przewidują wyniki meczów.",
                },
            ],
            pills: [
                "Wizualizacja kortu 3D z filtrami uderzeń",
                "Match Quality Score",
                "Analiza break pointów",
                "Kreator raportów",
                "Eksport CSV",
                "Filtrowanie po nawierzchni",
                "Auto-benchmarking na 10 meczach",
                "Automatyczne raporty PDF (e-mail po każdym meczu)",
                "Automatyczna synchronizacja ratingów WTN i UTR",
                "English / Español",
            ],
            proprietary: "Autorska",
            catalogTag: "Katalog · 24 metryki taktyczne",
            catalogTitle: "Wszystkie metryki śledzone przez Davini",
            catalogSub: "Każdy mecz generuje pełny zestaw metryk — we wszystkich 8 wymiarach.",
        },
        metrics: {
            tag: "04 — Metryki autorskie",
            headline: "Metryki stworzone, by odpowiedzieć na jedno pytanie:",
            headlineAccent: "co trenujemy dalej?",
            sub: "Metryki taktyczne stworzone specjalnie dla tenisa, oparte na metodologii trenerskiej Franco Davína. Poniższa przykładowa analiza wykorzystuje prawdziwe, zanonimizowane dane 18-letniego europejskiego juniora ITF (24+ śledzone mecze turniejowe). Pokazane w celach poglądowych.",
            semVerdict:
                '"Serwis prawdopodobnie zostanie przełamany w 1 na 3 gemy." Margines wygranych punktów serwisowych, który przewiduje, jak często będziesz przełamywany.',
            remVerdict:
                '"Wysokie prawdopodobieństwo przełamania rywala w 1 na 3 gemach." Margines wygranych punktów returnowych, który przewiduje, jak często będziesz przełamywać.',
            p1Verdict:
                "% błędów niewymuszonych, które są decydujące — błędów, które naprawdę rozstrzygnęły mecz.",
            leadershipLabel: "Leadership Margin",
            leadershipCaption: "10 meczów",
            leadershipPointsLabel: "Kontrolowane punkty",
            leadershipBody:
                "Kto kontrolował mecz punkt po punkcie — śledzone w 10+ meczach z rywalami.",
            rallyLabel: "Rally Win — najczęstszy przedział wymiany",
            rallyCaption: "57% wygranych @ 0–2 uderzenia",
            rallyBody:
                "57% punktów wygranych przy najczęstszej długości wymiany (0–2 uderzenia) — ujawnia idealną taktyczną długość gry zawodnika.",
            p1Heading: "P1 — Priorytet 1",
            proprietary: "Autorska",
            simTag: "Silnik symulacji",
            simTitle: "Zasymuluj poprawę, zanim ją wytrenujesz.",
            simBody: "Wybierz priorytetowy błąd. Zredukuj go. Davini przelicza na nowo Match Performance, S.E.M., R.E.M. i Minimatches na całej historii meczów — pokazując, jak bardzo każda korekta przesuwa wynik, zanim poświęcisz choć tydzień na korcie.",
            simCase: "Prawdziwy przypadek: były zawodnik z Top 40 ATP tracił ok. 12 punktów na mecz przez chwile dekoncentracji. Symulacja pokazała, że zejście do 7 daje margines wyników na poziomie Top 20 — celem treningowym stało się więc wygrywanie pojedynczego punktu po każdej takiej chwili. To metryka podjęła decyzję trenerską.",
            simChip: "Autorskie · Analiza what-if",
        },
        dashboard: {
            tag: "05 — Panel meczowy",
            headline: "Każdy mecz staje się",
            headlineAccent: "raportem taktycznym.",
            sub: "Interaktywna powtórka, błyskawiczne rankingi, radar umiejętności i historia uderzeń — każdy mecz kończy się jasnym wnioskiem: co działało, co nie, i co trenować w tym tygodniu.",
            playerLabel: "Przykładowy profil · junior ITF",
            playerSub: "Europejski junior ITF · 18 lat · Praworęczny",
            perfTitle: "Wyniki meczowe · Ostatnie 10",
            perfMargin: "pożądany margines: +6",
            skillsetTitle: "Skillset Radar",
            skillsetSub: "Profil 6-osiowy",
            fhbhTitle: "Kluczowe uderzenia FH / BH — historycznie",
            minimatchTitle: "Minimatches — skuteczność",
            qualityTitle: "Najwyższa jakość meczu",
            featurePills: [
                "Wizualizacja kortu 3D z filtrami uderzeń",
                "Match Quality Score",
                "6-osiowy Skillset Radar",
                "Analiza Minimatches",
                "Podział strona równa / przewag",
                "Analiza uderzeń FH / BH",
            ],
            forehand: "Forhend",
            backhand: "Bekhend",
        },
        pricing: {
            tag: "06 — Cennik",
            headline: "Plany dla zawodników, trenerów,",
            headlineAccent: "akademii i zespołów pro.",
            sub: "Ceny wczesnego dostępu dla trenerów i akademii. Davini skaluje się bez kosztów sprzętu, opłat instalacyjnych i ukrytych progów.",
            perMonth: "/mies.",
            popular: "Najpopularniejszy",
            earlyAccess: "Wczesny dostęp",
            tiers: [
                {
                    name: "Zawodnik",
                    price: "$29",
                    tagline: "Najlepszy dla zawodników turniejowych śledzących własny rozwój.",
                    features: [
                        "Osobisty panel analityki meczowej",
                        "Do 10 meczów / miesiąc",
                        "Wszystkie 8 wymiarów analitycznych",
                        "Trendy wyników z ostatnich 10 meczów",
                        "Analiza błędów P1",
                        "Radar umiejętności i wykresy długości wymian",
                        "Raporty przyjazne urządzeniom mobilnym",
                    ],
                    cta: "Poproś o dostęp",
                },
                {
                    name: "Trener",
                    price: "$99",
                    tagline:
                        "Najlepszy dla trenerów prywatnych i podróżujących, prowadzących wielu zawodników.",
                    features: [
                        "Wszystko z planu Zawodnik",
                        "Nielimitowane mecze",
                        "Zarządzanie wieloma zawodnikami (do 10)",
                        "Pełne metryki S.E.M. / R.E.M.",
                        "Analiza błędów P1",
                        "Własny kreator raportów",
                        "Priorytetowe wsparcie",
                    ],
                    cta: "Poproś o dostęp",
                },
                {
                    name: "Akademia i Pro",
                    price: "$499",
                    tagline: "Najlepszy dla akademii tenisowych, programów federacji i zespołów zawodowych.",
                    features: [
                        "Wszystko z planu Trener",
                        "Nielimitowane profile zawodników",
                        "Analityka i benchmarking całego zespołu (wkrótce)",
                        "Dostęp do API dla własnych integracji (wkrótce)",
                        "Dedykowany opiekun konta",
                        "Raporty white-label (wkrótce)",
                    ],
                    cta: "Porozmawiaj z nami",
                },
            ],
        },
        competitive: {
            tag: "07 — Co wnosimy",
            headline: "Czym Davini.ai",
            headlineAccent: "się różni.",
            sub: "Inne platformy odpowiadają na pytanie „co się stało?”. Davini odpowiada „co robimy dalej?” — elitarna metodologia, inteligencja taktyczna stworzona dla tenisa i zautomatyzowane werdykty trenerskie w jednym przepływie pracy. Bez sprzętu, bez instalacji.",
            feature: "Funkcjonalność",
            cols: ["Davini.ai", "Hawk-Eye", "SwingVision", "Statystyki ręczne"],
            rows: [
                "Analityka punkt po punkcie",
                "8 taktycznych wymiarów analitycznych",
                "Metryki S.E.M. / R.E.M.",
                "Klasyfikacja błędów P1",
                "Trendy wielomeczowe (10+)",
                "Zautomatyzowane werdykty trenerskie (skalibrowane przez wielkoszlemowego trenera)",
                "Dostępne dla indywidualnych trenerów",
                "Działa bez sprzętu",
                "Przepływ pracy zaprojektowany przez trenerów",
            ],
            partial: "częściowo",
            quote: "Zbudowane na metodologii trenerskiej",
            quoteAccent: "Franco Davína — trenera dwóch mistrzów wielkoszlemowych: Juana Martína del Potro (US Open 2009) i Gastóna Gaudio (Roland Garros 2004).",
            quoteRest: "",
            tagline: "Davini.ai — każda platforma daje Ci dane. Davini mówi, co wygrywa mecze.",
            footnote:
                "Porównanie oparte na publicznie dostępnych informacjach o produktach (stan na grudzień 2025). Produkty konkurencji wciąż się rozwijają — przed zakupem prosimy o weryfikację.",
        },
        traction: {
            tag: "08 — Trakcja",
            headline: "Już teraz robimy",
            headlineAccent: "różnicę.",
            stats: [
                ["20+", "Wspieranych zawodowych graczy"],
                ["1 100+", "Przeanalizowanych meczów turniejowych"],
                ["230+", "Metryk wydajności na mecz"],
                ["250 000+", "Obliczonych statystyk meczowych"],
            ],
            milestones: [
                "Współtworzone z Franco Davínem — byłym trenerem dwóch mistrzów wielkoszlemowych: Juana Martína del Potro (US Open 2009) i Gastóna Gaudio (Roland Garros 2004).",
                "Zweryfikowane z juniorami klasyfikowanymi przez ITF na kortach twardych, ziemnych i halowych.",
                "Platforma działa produkcyjnie ze wszystkimi 8 wymiarami analitycznymi.",
            ],
            trustedTitle: "Zaufanie na każdym poziomie gry",
            trustedItems: [
                "Zawodowcy tourów WTA i ATP — w tym kilku graczy z Top 20",
                "Aktualny juniorski mistrz wielkoszlemowy",
                "Juniorzy klasyfikowani przez ITF na trzech kontynentach",
            ],
            orgsNote: "Współpraca badawcza z Międzynarodową Federacją Tenisową (ITF). Używany również przez reprezentacje narodowe Pucharu Davisa i Billie Jean King Cup, COSAT (Południowoamerykańską Konfederację Tenisa) oraz czołowe akademie w całej Ameryce Południowej.",
        },
        team: {
            tag: "09 — Zespół",
            headline: "Stworzone przez trenerów,",
            headlineAccent: "dla trenerów.",
            sub: "Davini.ai narodziło się na kortach treningowych elitarnego tenisa. Nie zbudowaliśmy kolejnej aplikacji ze statystykami — zbudowaliśmy platformę analityczną, której brakowało nam, gdy trenowaliśmy na najwyższym poziomie.",
            members: [
                {
                    name: "Franco Davín",
                    role: "Współzałożyciel i CEO",
                    sub: "Szef metodologii trenerskiej",
                    bio: "Były trener dwóch mistrzów wielkoszlemowych — Juana Martína del Potro (US Open 2009) i Gastóna Gaudio (Roland Garros 2004). Dziesięciolecia doświadczenia trenerskiego w ATP Tour. Architekt frameworku analitycznego stojącego za każdą metryką Davini — P1, S.E.M., R.E.M., Leadership Margin.",
                },
                {
                    name: "Boon Chew",
                    role: "Współzałożyciel i CTO",
                    sub: "Produkt i inżynieria",
                    bio: "Boon Chew to venture builder i założyciel Tappollo Media oraz Centaur Lab, skoncentrowany na uruchamianiu i skalowaniu firm technologicznych i AI. Pomógł tworzyć platformy stojące za startupami wycenianymi łącznie na ponad 3 miliardy dolarów i współpracował z globalnymi markami, m.in. iHeartRadio, Discovery, CLEAR i SEVENROOMS. Jego dorobek obejmuje szybko rosnące produkty konsumenckie i nagradzane platformy społeczne, w tym pierwszą aplikację randkową dla społeczności osób ze spektrum autyzmu oraz Pepper — platformę społecznościową z ponad 2 milionami pobrań.",
                },
            ],
        },
        cta: {
            eyebrow: "Gotowy trenować jak profesjonalista?",
            headlinePart1: "Wygraj mecz,",
            headlineAccent: "zanim",
            headlinePart2: "się zacznie.",
            sub: "Poproś o wczesny dostęp. Wgraj jeden mecz i otrzymaj pierwszy werdykt skalibrowany przez trenera — co trenować, dlaczego i ile to jest warte.",
            primary: "Poproś o wczesny dostęp",
            secondary: "Porozmawiaj z nami",
            footnote:
                "Przepływ pracy z perspektywy trenera · Bez sprzętu · Zaprojektowane wokół metodologii Franco Davína",
        },
        footer: {
            copyright: "© {year} Davini.ai — Taktyczna analityka dla tenisa.",
            product: "Produkt",
            pricing: "Cennik",
            team: "Zespół",
        },
        presenter: {
            slides: [
                "Okładka",
                "Problem",
                "Rozwiązanie",
                "8 wymiarów",
                "Metryki autorskie",
                "Panel meczowy",
                "Cennik",
                "Przewaga konkurencyjna",
                "Trakcja",
                "Zespół",
                "Zakończenie",
            ],
            navigate: "nawigacja",
            agenda: "agenda",
            exit: "wyjście",
            agendaHeader: "Agenda",
            agendaTitle: "Przejdź do dowolnego slajdu",
            agendaTip: "Wskazówka — naciśnij 1–9, aby przejść bezpośrednio",
            agendaClose: "zamknij",
            slideLabel: "Slajd",
        },
        export: {
            title: "Eksportowanie PDF",
            renderingSlide: "Renderowanie slajdu",
            of: "z",
            keepOpen: "Trwa przechwytywanie wszystkich 11 slajdów — nie zamykaj tej karty.",
        },
        langSwitcher: { label: "Język" },
    },
};

export const LANGUAGES = [
    { code: "en", label: "EN", name: "English" },
    { code: "es", label: "ES", name: "Español" },
    { code: "fr", label: "FR", name: "Français" },
    { code: "zh", label: "中文", name: "简体中文" },
    { code: "pl", label: "PL", name: "Polski" },
];
